export type WhiteLabelImageKind = 'logo' | 'favicon' | 'tenantAlias';

// Mesmos tamanhos do white-label montado à mão (white-label/<tema>/images/).
export const WHITE_LABEL_IMAGE_SPECS: Record<
  WhiteLabelImageKind,
  { width: number; height: number }
> = {
  logo: { width: 300, height: 100 },
  favicon: { width: 64, height: 64 },
  tenantAlias: { width: 192, height: 192 },
};

// Lado do quadrado onde o símbolo entra no tenant-alias, em relação ao
// diâmetro do círculo (o quadrado inscrito seria ~0.707; sobra uma folga).
const TENANT_ALIAS_INNER_RATIO = 0.65;

/** Encaixa a imagem inteira dentro da caixa, sem esticar, centralizada. */
export function computeContainRect(
  srcWidth: number,
  srcHeight: number,
  boxWidth: number,
  boxHeight: number,
) {
  const scale = Math.min(boxWidth / srcWidth, boxHeight / srcHeight);
  const width = Math.round(srcWidth * scale);
  const height = Math.round(srcHeight * scale);

  return {
    x: Math.round((boxWidth - width) / 2),
    y: Math.round((boxHeight - height) / 2),
    width,
    height,
  };
}

// Rede de segurança: se o decode não terminar nem falhar, a promise ainda
// rejeita em vez de deixar quem chamou preso num carregando sem fim.
const LOAD_TIMEOUT_MS = 20000;

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();

    const timer = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      reject(new Error('Tempo esgotado ao abrir a imagem'));
    }, LOAD_TIMEOUT_MS);

    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error('Imagem inválida'));
    };

    // src só depois dos handlers: imagem já decodificada dispara o evento na hora.
    img.src = src;
  });

// Cor do canto superior esquerdo, se for opaca: a sobra da logo fica com o
// fundo da própria logo em vez de transparente.
const getOpaqueCornerColor = (img: HTMLImageElement): string | null => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(img, 0, 0, 1, 1, 0, 0, 1, 1);
  const [r, g, b, a] = Array.from(ctx.getImageData(0, 0, 1, 1).data);

  return a === 255 ? `rgb(${r}, ${g}, ${b})` : null;
};

/**
 * Gera o PNG no tamanho exato de cada imagem do white-label:
 * - logo 300x100: logo inteira sem esticar, sobra com a cor de fundo da logo
 * - favicon 64x64: imagem inteira, fundo transparente
 * - tenantAlias 192x192: círculo branco com o símbolo dentro, cantos transparentes
 */
export async function renderWhiteLabelImage(
  src: string,
  kind: WhiteLabelImageKind,
): Promise<string> {
  const img = await loadImage(src);
  const { width, height } = WHITE_LABEL_IMAGE_SPECS[kind];

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas indisponível');

  ctx.imageSmoothingQuality = 'high';

  let box = { x: 0, y: 0, width, height };

  if (kind === 'logo') {
    const background = getOpaqueCornerColor(img);
    if (background) {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, width, height);
    }
  }

  if (kind === 'tenantAlias') {
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, width / 2, 0, Math.PI * 2);
    ctx.fill();

    const inner = Math.round(width * TENANT_ALIAS_INNER_RATIO);
    box = {
      x: Math.round((width - inner) / 2),
      y: Math.round((height - inner) / 2),
      width: inner,
      height: inner,
    };
  }

  const rect = computeContainRect(
    img.naturalWidth,
    img.naturalHeight,
    box.width,
    box.height,
  );

  ctx.drawImage(img, box.x + rect.x, box.y + rect.y, rect.width, rect.height);

  return canvas.toDataURL('image/png');
}
